import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton as MuiIconButton,
  InputAdornment,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import {RootState} from '../../types';
import {
  clearClientError,
  fetchClientById,
  fetchCrowdstrikeTickets,
  updateClient
} from '../../store/slices/clientsSlice';
import ClientPolicyPanel from './ClientPolicyPanel';
import AssetManagementPanel from './AssetManagementPanel';

const ClientDetail: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
    const {
        selectedClient,
        isLoading,
        error,
        isFetchingCrowdstrikeTickets,
        crowdstrikeTicketsMessage
    } = useSelector((state: RootState) => state.clients);
  const { user } = useSelector((state: RootState) => state.auth);

  // State for edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
    const [crowdstrikeClientId, setCrowdstrikeClientId] = useState('');
    const [crowdstrikeClientSecret, setCrowdstrikeClientSecret] = useState('');
    const [showClientSecret, setShowClientSecret] = useState(false);

  const [activeTab, setActiveTab] = useState(0);

  // Check if user is admin
  const isAdmin = user?.role === 'global_admin';

  // Fetch client on component mount
  useEffect(() => {
    if (clientId) {
      dispatch(fetchClientById(clientId) as any);
    }

    // Cleanup on unmount
    return () => {
      dispatch(clearClientError());
    };
  }, [dispatch, clientId]);

  // Update form values when client data changes
  useEffect(() => {
    if (selectedClient) {
      setName(selectedClient.name);
      setDescription(selectedClient.description || '');
        setCrowdstrikeClientId(selectedClient.crowdstrike_client_id || '');
        setCrowdstrikeClientSecret(selectedClient.crowdstrike_client_secret || '');
    }
  }, [selectedClient]);

  // Handle back button click
  const handleBack = () => {
    navigate('/clients');
  };

  // Handle edit button click
  const handleEditClick = () => {
    setIsEditMode(true);
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditMode(false);
    // Reset form values
    if (selectedClient) {
      setName(selectedClient.name);
      setDescription(selectedClient.description || '');
    }
  };

  // Handle save changes
  const handleSaveChanges = () => {
    if (clientId && name.trim()) {
      dispatch(updateClient({
        clientId,
        clientData: {
          name: name.trim(),
          description: description.trim() || undefined,
            crowdstrike_client_id: crowdstrikeClientId.trim() || null,
            crowdstrike_client_secret: crowdstrikeClientSecret.trim() || null,
        },
      }) as any).then((result: any) => {
        if (!result.error) {
          setIsEditMode(false);
        }
      });
    }
  };

  // Handle manage access
  const handleManageAccess = () => {
    if (clientId) {
      navigate(`/clients/${clientId}/access`);
    }
  };

    // Handle fetch tickets from CrowdStrike
    const handleFetchCrowdstrikeTickets = () => {
        if (clientId) {
            dispatch(fetchCrowdstrikeTickets(clientId) as any);
        }
    };

  // Clear error
  const handleClearError = () => {
    dispatch(clearClientError());
  };

  // If loading and no client data, show loading indicator
  if (isLoading && !selectedClient) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  // If no client found, show error
  if (!selectedClient && !isLoading) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 3 }}>
          Back to Clients
        </Button>
        <Alert severity="error">
          Client not found or you don't have access to view it.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Clients
        </Button>
        {isAdmin && !isEditMode && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            onClick={handleEditClick}
          >
            Edit Client
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={handleClearError} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Overview" />
        <Tab label="Policy" />
        <Tab label="Assets" />
      </Tabs>

      {/* Policy tab */}
      {activeTab === 1 && clientId && <ClientPolicyPanel clientId={clientId} />}

      {/* Assets tab */}
      {activeTab === 2 && clientId && <AssetManagementPanel clientId={clientId} />}

      {/* Overview tab */}
      {activeTab === 0 && <Paper sx={{ p: 3, mb: 3 }}>
        {isEditMode ? (
          // Edit mode
          <Box>
            <Typography variant="h5" component="h1" gutterBottom>
              Edit Client
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid size = {{xs: 12}}>
                <TextField
                  label="Client Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  required
                  error={!name.trim()}
                  helperText={!name.trim() ? 'Client name is required' : ''}
                />
              </Grid>
              <Grid size = {{xs: 12}}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                />
              </Grid>
                <Grid size = {{xs: 12}}>
                    <Typography variant="h6" gutterBottom>
                        CrowdStrike Integration
                    </Typography>
                    <Typography variant="body2" color="textSecondary" paragraph>
                        Configure CrowdStrike API credentials to enable automatic ticket creation from CrowdStrike
                        detections.
                    </Typography>
                </Grid>
                <Grid size = {{xs: 12, md: 6}}>
                    <TextField
                        label="CrowdStrike Client ID"
                        value={crowdstrikeClientId}
                        onChange={(e) => setCrowdstrikeClientId(e.target.value)}
                        fullWidth
                        placeholder="Enter CrowdStrike API Client ID"
                    />
                </Grid>
                <Grid size = {{xs: 12, md: 6}}>
                    <TextField
                        label="CrowdStrike Client Secret"
                        value={crowdstrikeClientSecret}
                        onChange={(e) => setCrowdstrikeClientSecret(e.target.value)}
                        type={showClientSecret ? 'text' : 'password'}
                        fullWidth
                        placeholder="Enter CrowdStrike API Client Secret"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <MuiIconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowClientSecret(!showClientSecret)}
                                        edge="end"
                                    >
                                        {showClientSecret ? <VisibilityOffIcon/> : <VisibilityIcon/>}
                                    </MuiIconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
              </Grid>
              <Grid size = {{xs: 12}}>
                <Box display="flex" justifyContent="flex-end" gap={1}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveChanges}
                    disabled={!name.trim()}
                  >
                    Save Changes
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        ) : (
          // View mode
          <Box>
            <Typography variant="h5" component="h1" gutterBottom>
              {selectedClient?.name}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid size = {{xs: 12, sm: 6}}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Description
                </Typography>
                <Typography variant="body1">
                  {selectedClient?.description || 'No description provided.'}
                </Typography>
              </Grid>
              <Grid size = {{xs: 12, sm: 6}}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Created At
                </Typography>
                <Typography variant="body1">
                  {selectedClient?.created_at ? new Date(selectedClient.created_at).toLocaleString() : '-'}
                </Typography>
              </Grid>
              <Grid size = {{xs: 12, sm: 6}}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Last Updated
                </Typography>
                <Typography variant="body1">
                  {selectedClient?.updated_at ? new Date(selectedClient.updated_at).toLocaleString() : '-'}
                </Typography>
              </Grid>
              <Grid size = {{xs: 12, sm: 6}}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Tickets Count
                </Typography>
                <Typography variant="body1">
                  {selectedClient?.tickets_count || 0}
                </Typography>
              </Grid>
                <Grid size = {{xs: 12}}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        CrowdStrike Integration
                    </Typography>
                    <Typography variant="body1">
                        {selectedClient?.crowdstrike_client_id ? 'Configured' : 'Not configured'}
                </Typography>
              </Grid>
              {isAdmin && (
                <Grid size = {{xs: 12}}>
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleManageAccess}
                    >
                      Manage User Access
                    </Button>
                        {selectedClient?.crowdstrike_client_id && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<RefreshIcon/>}
                                onClick={handleFetchCrowdstrikeTickets}
                                disabled={isFetchingCrowdstrikeTickets}
                            >
                                {isFetchingCrowdstrikeTickets ? 'Fetching...' : 'Fetch CrowdStrike Tickets'}
                            </Button>
                        )}
                  </Box>
                </Grid>
              )}
                {crowdstrikeTicketsMessage && (
                    <Grid size = {{xs: 12}}>
                        <Alert severity="info" onClose={() => dispatch(clearClientError())}>
                            {crowdstrikeTicketsMessage}
                        </Alert>
                    </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Paper>}
    </Box>
  );
};

export default ClientDetail;
